/**
 * Embebido de clase en vivo Jitsi (F3-G). Carga el `external_api.js` del DOMINIO
 * configurado (lo resuelve el servidor) y monta el iframe con la sala
 * determinista, el displayName del usuario y el prejoin desactivado.
 *
 * El script es EXTERNO (no lo embarca el bundle): se inyecta perezosamente. En
 * tests/e2e se intercepta la red de `external_api.js` con un stub que define
 * `window.JitsiMeetExternalAPI` (no se golpea meet.jit.si en CI). El botón de
 * "salir" del propio iframe (evento `readyToClose`) o el desmontaje disponen la
 * instancia y avisan con `onLeave`.
 */
import { type Component, createSignal, onCleanup, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { RefreshCw } from "lucide-solid";
import { buildJitsiEmbedOptions, jitsiExternalApiUrl } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";

/** Firma mínima de la External API que usamos. */
interface JitsiApiInstance {
  addEventListener(event: string, handler: () => void): void;
  dispose(): void;
}
type JitsiApiCtor = new (domain: string, options: Record<string, unknown>) => JitsiApiInstance;

declare global {
  interface Window {
    JitsiMeetExternalAPI?: JitsiApiCtor;
  }
}

export interface JitsiEmbedProps {
  domain: string;
  roomName: string;
  displayName?: string | null;
  /** Se llama cuando el usuario sale de la reunión (evento del iframe). */
  onLeave?: () => void;
}

/** Carga (una vez por dominio) el `external_api.js`. Rechaza si el script falla. */
function loadJitsiScript(domain: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) {
      resolve();
      return;
    }
    const src = jitsiExternalApiUrl(domain);
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("jitsi script failed")));
      // Si ya cargó antes de este listener.
      if (window.JitsiMeetExternalAPI) {
        resolve();
      }
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("jitsi script failed")));
    document.head.append(script);
  });
}

export const JitsiEmbed: Component<JitsiEmbedProps> = (props) => {
  const { t } = useI18n();
  const [failed, setFailed] = createSignal(false);
  const [ready, setReady] = createSignal(false);
  let container: HTMLDivElement | undefined;
  let apiInstance: JitsiApiInstance | null = null;

  onMount(() => {
    void (async () => {
      try {
        await loadJitsiScript(props.domain);
        const Ctor = window.JitsiMeetExternalAPI;
        if (!Ctor || !container) {
          setFailed(true);
          return;
        }
        const options = buildJitsiEmbedOptions({
          roomName: props.roomName,
          displayName: props.displayName,
        });
        apiInstance = new Ctor(props.domain, {
          ...options,
          parentNode: container,
          width: "100%",
          height: "100%",
        });
        apiInstance.addEventListener("readyToClose", () => props.onLeave?.());
        setReady(true);
      } catch {
        setFailed(true);
      }
    })();
  });

  onCleanup(() => {
    try {
      apiInstance?.dispose();
    } catch {
      /* ya dispuesto */
    }
    apiInstance = null;
  });

  return (
    <div
      class="relative aspect-video w-full overflow-hidden rounded-2xl bg-black"
      data-testid="jitsi-embed"
      data-embed-ready={ready() ? "true" : "false"}
    >
      <Show
        when={!failed()}
        fallback={
          <div
            class="flex h-full w-full flex-col items-center justify-center gap-2 p-6 text-center text-white"
            data-testid="jitsi-embed-error"
          >
            <p class="text-sm font-medium">{t(msg`The live class could not be loaded.`)}</p>
          </div>
        }
      >
        <div ref={container} class="h-full w-full" data-testid="jitsi-embed-frame" />
        <Show when={!ready()}>
          <div
            class="pointer-events-none absolute inset-0 flex items-center justify-center"
            data-testid="jitsi-embed-loading"
          >
            <span class="motion-safe:animate-spin">
              <RefreshCw class="h-8 w-8 text-white/70" />
            </span>
          </div>
        </Show>
      </Show>
    </div>
  );
};
